import React, { useEffect, useRef } from 'react';

import { Box, ButtonBase } from '@mui/material';
import PropTypes from 'prop-types';

import styles from './announcementVideo.module.css';

import { ReactComponent as ExpandIcon } from 'asset/IconsPack/at_expand-01-filled.svg';

// A muted loop with a "Full screen" button that shows on hover, to watch the clip with controls.
const AnnouncementVideo = ({ src, poster, className }) => {
    const videoRef = useRef(null);

    // Leaving full screen hides the controls again and keeps the loop playing.
    useEffect(() => {
        const video = videoRef.current;
        const handleExit = () => {
            if (document.fullscreenElement === video) return;
            video.controls = false;
            video.play().catch(() => {});
        };
        document.addEventListener('fullscreenchange', handleExit);
        video.addEventListener('webkitendfullscreen', handleExit);
        return () => {
            document.removeEventListener('fullscreenchange', handleExit);
            video.removeEventListener('webkitendfullscreen', handleExit);
        };
    }, []);

    const openFullScreen = () => {
        const video = videoRef.current;
        video.currentTime = 0;
        video.controls = true;
        if (video.requestFullscreen) {
            video.requestFullscreen().catch(() => {
                video.controls = false;
            });
        } else if (video.webkitEnterFullscreen) {
            // iPhone Safari only allows full screen through the video element itself.
            video.webkitEnterFullscreen();
        }
        video.play().catch(() => {});
    };

    return (
        <Box className={styles.wrap}>
            <video
                ref={videoRef}
                className={className}
                src={src}
                poster={poster}
                // Decorative: the text below already says what the clip shows.
                aria-hidden="true"
                tabIndex={-1}
                autoPlay
                muted
                loop
                playsInline
            />
            <ButtonBase
                className={styles.fullScreen}
                onClick={openFullScreen}
                aria-label="Watch the video full screen"
            >
                <ExpandIcon className={styles.icon} />
                Full screen
            </ButtonBase>
        </Box>
    );
};

AnnouncementVideo.propTypes = {
    src: PropTypes.string.isRequired,
    poster: PropTypes.string,
    className: PropTypes.string,
};

export default AnnouncementVideo;
